/*
 * Inka (agente E) · carrito, añadir rápido, barra «Ver pedido» y formulario de la ficha.
 * Módulo ES (type="module" = diferido) cargado en layout/theme.liquid. Sin librerías.
 *
 * Integración con Horizon 4.1.5 (ver _taller/notas-agente-E.md):
 *  - Al añadir se emite `CartLinesUpdateEvent` (@shopify/events, 'shopify:cart:lines-update') con una
 *    promesa que se resuelve con { cart, detail: { sections, items, didError } }. Con eso Horizon
 *    actualiza solo el contador (cart-icon), el drawer y la página /cart (cart-items-component), y
 *    abre el drawer si el ajuste «auto_open_cart_drawer» está activo. Es lo mismo que hace product-form.js.
 *  - /cart/add.js se pide con `sections` = ids de los <cart-items-component> de la página, para que el
 *    drawer se re-pinte sin otra petición.
 *  - El drawer es <theme-drawer id="cart-drawer"> (open()/close(), atributo [open]).
 *
 * API pública: window.InkaCart = { add, fetchCart, openCart, openQuickAdd, refresh }
 * Eventos que emite: 'inka:variant-change' y 'inka:added' (en <inka-producto-form>, burbujean).
 * Escucha: 'inka:quick-add' en document (lo emite assets/inka-card.js, agente A).
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';
import { sectionRenderer } from '@theme/section-renderer';

const ROOT = window.Shopify?.routes?.root || '/';
// `Theme` es un const global de snippets/scripts.liquid (accesible por nombre, no como window.Theme).
const THEME_ROUTES = typeof Theme !== 'undefined' && Theme.routes ? Theme.routes : null;
const ROUTES = {
  add: THEME_ROUTES?.cart_add_url || `${ROOT}cart/add.js`,
  cart: `${THEME_ROUTES?.cart_url || `${ROOT}cart`}.js`,
  cartPage: THEME_ROUTES?.cart_url || `${ROOT}cart`,
};
const MOBILE = window.matchMedia('(max-width: 749px)');
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- utilidades ---------- */

function money(cents, el) {
  const format = el?.dataset?.moneyFormat || '{{amount_with_comma_separator}} €';
  const currency = el?.dataset?.currency || 'EUR';
  try {
    return formatMoney(Number(cents) || 0, format, currency);
  } catch (_) {
    return `${((Number(cents) || 0) / 100).toFixed(2).replace('.', ',')} €`;
  }
}

function announce(text) {
  let live = document.getElementById('inka-live');
  if (!live) {
    live = document.createElement('div');
    live.id = 'inka-live';
    live.className = 'inka-visually-hidden';
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    document.body.append(live);
  }
  live.textContent = '';
  requestAnimationFrame(() => {
    live.textContent = text;
  });
}

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
}

async function fetchCart() {
  const res = await fetch(ROUTES.cart, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!res.ok) throw new Error(`cart.js ${res.status}`);
  return res.json();
}

/* ---------- añadir al carrito (mismo contrato de eventos que product-form.js de Horizon) ---------- */

/**
 * @param {Array<{id:number|string, quantity?:number, properties?:Record<string,string>}>} items
 * @param {{source?: Element}} [options] - Elemento que origina la acción (botón). Si está dentro de un
 *   <dialog> modal, Horizon espera a que se cierre antes de abrir el drawer.
 */
async function add(items, { source } = {}) {
  const target = source instanceof Element && source.isConnected ? source : document;
  const lines = items.map((i) => ({ merchandiseId: String(i.id), quantity: Number(i.quantity) || 1 }));
  const itemCount = lines.reduce((n, l) => n + l.quantity, 0);
  const deferred = CartLinesUpdateEvent.createPromise();
  let settled = false;

  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: target instanceof Element && target.closest('dialog') ? 'dialog' : 'product',
      lines,
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(ROUTES.add, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        items: items.map((i) => ({
          id: Number(i.id),
          quantity: Number(i.quantity) || 1,
          ...(i.properties ? { properties: i.properties } : {}),
        })),
        sections: cartSectionIds().join(','),
      }),
    });
    const data = await res.json();
    const cart = await fetchCart();

    if (data.status) {
      target.dispatchEvent(
        new CartErrorEvent({
          error: data.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: data.description, errors: data.errors },
        })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'inka-carrito', itemCount },
      });
      settled = true;
      const err = new Error(data.description || data.message || '');
      err.name = 'InkaCartError';
      throw err;
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: cart.items,
        source: 'inka-carrito',
        sourceId: 'inka-carrito',
        itemCount,
        sections: data.sections,
      },
    });
    settled = true;
    VerPedido.render(cart);
    return cart;
  } catch (error) {
    if (!settled) {
      deferred.reject(error);
      target.dispatchEvent(
        new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
      );
    }
    throw error;
  }
}

function openCart() {
  const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
  } else {
    window.location.href = ROUTES.cartPage;
  }
}

async function rerenderCartSections(modo) {
  const ids = cartSectionIds();
  if (!ids.length) return;
  for (let i = 0; i < 4; i++) {
    await new Promise((r) => setTimeout(r, 250 * (i + 1)));
    try {
      const cart = await fetchCart();
      if (!modo || cart.attributes?.modo_pedido === modo) break;
    } catch (_) {
      /* reintenta */
    }
  }
  for (const id of ids) {
    const mode = id === 'cart-drawer-section' ? 'hydration' : 'full';
    try {
      sectionRenderer.renderSection(id, { cache: false, mode });
    } catch (_) {
      /* sin re-pintado: se verá al siguiente cambio */
    }
  }
}

/* ---------- <inka-producto-form>: píldoras, cantidad, precio, nota y envío ---------- */

class InkaProductoForm extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.form = this.querySelector('form');
    this.variants = [];
    try {
      this.variants = JSON.parse(this.querySelector('[data-inka-variants]')?.textContent || '[]');
    } catch (_) {
      this.variants = [];
    }
    this.idInput = this.querySelector('[data-inka-variant-input]');
    this.submitBtn = this.querySelector('[data-inka-submit]');
    this.submitLabel = this.querySelector('[data-inka-submit-label]');
    this.submitPrice = this.querySelector('[data-inka-submit-price]');
    this.priceEl = this.querySelector('[data-inka-price]');
    this.statusEl = this.querySelector('[data-inka-status]');
    this.qtyInput = this.querySelector('input[name="quantity"]');
    this.current =
      this.variants.find((v) => String(v.id) === String(this.idInput?.value)) || this.variants[0] || null;

    this.addEventListener('change', (e) => {
      const t = e.target;
      if (t instanceof HTMLInputElement && t.classList.contains('inka-pill__input')) this.#onOptionChange();
      if (t === this.qtyInput) this.#setQty(this.qtyInput.value);
    });
    this.addEventListener('click', (e) => {
      const btn = e.target instanceof Element ? e.target.closest('[data-qty-step]') : null;
      if (btn instanceof HTMLElement) this.#setQty(this.quantity + Number(btn.dataset.qtyStep));
    });
    this.form?.addEventListener('submit', (e) => this.#onSubmit(e));
    this.#render();
  }

  get quantity() {
    return Math.min(99, Math.max(1, parseInt(this.qtyInput?.value || '1', 10) || 1));
  }

  /** 'ok' | 'soon' (precio 0) | 'soldout' | 'unavailable' (combinación inexistente) */
  get state() {
    const v = this.current;
    if (!v) return 'unavailable';
    if (Number(v.price) === 0) return 'soon';
    if (!v.available) return 'soldout';
    return 'ok';
  }

  #setQty(value) {
    if (!this.qtyInput) return;
    const n = Math.min(99, Math.max(1, parseInt(String(value), 10) || 1));
    this.qtyInput.value = String(n);
    this.#render();
  }

  #onOptionChange() {
    const groups = [...this.querySelectorAll('.inka-pf__option')];
    const selected = groups.map((g) => /** @type {HTMLInputElement|null} */ (g.querySelector('input:checked'))?.value);
    const variant =
      this.variants.find((v) => v.options.every((opt, i) => selected[i] === undefined || opt === selected[i])) || null;
    this.current = variant;
    if (variant && this.idInput) this.idInput.value = String(variant.id);

    if (variant && this.dataset.context === 'pdp') {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (_) {
        /* sin URL, nada */
      }
    }
    this.#render();
    this.dispatchEvent(new CustomEvent('inka:variant-change', { bubbles: true, detail: { variant, form: this } }));
  }

  #render() {
    const d = this.dataset;
    const v = this.current;
    const state = this.state;

    if (this.priceEl) {
      const span = document.createElement('span');
      if (state === 'soon' || !v) {
        span.className = 'inka-pf__price-soon';
        span.textContent = d.txtSoonPrice || '';
      } else {
        span.className = 'inka-pf__price-value';
        span.textContent = money(v.price, this);
      }
      this.priceEl.replaceChildren(span);
    }

    if (this.submitBtn && !this._busy) {
      const ok = state === 'ok';
      this.submitBtn.disabled = !ok;
      if (ok) this.submitBtn.removeAttribute('aria-disabled');
      else this.submitBtn.setAttribute('aria-disabled', 'true');
      if (this.submitLabel) {
        this.submitLabel.textContent =
          state === 'ok' ? d.txtAdd || '' : state === 'soon' ? d.txtSoonButton || '' : d.txtSoldOut || '';
      }
      if (this.submitPrice) this.submitPrice.textContent = ok && v ? `· ${money(v.price * this.quantity, this)}` : '';
    }
  }

  async #onSubmit(event) {
    event.preventDefault();
    if (this._busy || this.state !== 'ok' || !this.current) return;
    this._busy = true;
    const btn = this.submitBtn;
    btn?.setAttribute('aria-busy', 'true');
    btn?.classList.add('is-loading');
    if (this.statusEl) this.statusEl.textContent = '';

    const quantity = this.quantity;
    const noteField = /** @type {HTMLTextAreaElement|null} */ (this.form?.querySelector('[name="properties[Nota]"]'));
    const note = (noteField?.value || '').trim();
    const item = { id: this.current.id, quantity, ...(note ? { properties: { Nota: note } } : {}) };

    try {
      await add([item], { source: btn || this });
      btn?.classList.remove('is-loading');
      btn?.classList.add('is-done');
      if (this.submitLabel) this.submitLabel.textContent = `✓ ${this.dataset.txtAdded || ''}`;
      if (this.submitPrice) this.submitPrice.textContent = '';
      announce(this.dataset.txtAdded || '');
      this.dispatchEvent(
        new CustomEvent('inka:added', { bubbles: true, detail: { variant: this.current, quantity, note } })
      );
      if (noteField) noteField.value = '';
      await new Promise((r) => setTimeout(r, 1400));
    } catch (error) {
      const msg = error?.message || document.querySelector('[data-inka-qa]')?.getAttribute('data-txt-error') || '';
      if (this.statusEl) this.statusEl.textContent = msg;
    } finally {
      this._busy = false;
      btn?.removeAttribute('aria-busy');
      btn?.classList.remove('is-loading', 'is-done');
      this.#render();
    }
  }
}

if (!customElements.get('inka-producto-form')) customElements.define('inka-producto-form', InkaProductoForm);

/* ---------- Barra fija de la ficha (móvil) ---------- */

function initPdpBars() {
  document.querySelectorAll('[data-inka-pdp-bar]').forEach((bar) => {
    if (!(bar instanceof HTMLElement) || bar.dataset.inkaReady) return;
    bar.dataset.inkaReady = '1';
    const form = document.getElementById(bar.dataset.formId || '');
    const pf = /** @type {InkaProductoForm|null} */ (form?.closest('inka-producto-form') || null);
    const mainBtn = pf?.querySelector('[data-inka-submit]');
    const btn = /** @type {HTMLButtonElement|null} */ (bar.querySelector('[data-inka-bar-submit]'));
    const priceEl = bar.querySelector('[data-inka-bar-price]');
    if (!pf || !mainBtn || !btn) return;

    const sync = () => {
      const v = pf.current;
      const state = pf.state;
      if (priceEl) priceEl.textContent = state === 'soon' || !v ? pf.dataset.txtSoonPrice || '' : money(v.price, pf);
      btn.disabled = state !== 'ok';
      btn.textContent =
        state === 'ok' ? pf.dataset.txtAdd || '' : state === 'soon' ? pf.dataset.txtSoonButton || '' : pf.dataset.txtSoldOut || '';
    };

    btn.addEventListener('click', () => {
      if (pf.state === 'ok' && form instanceof HTMLFormElement) form.requestSubmit();
    });
    pf.addEventListener('inka:variant-change', sync);
    pf.addEventListener('inka:added', () => {
      btn.textContent = `✓ ${pf.dataset.txtAdded || ''}`;
      setTimeout(sync, 1400);
    });

    const setVisible = (show) => {
      bar.classList.toggle('is-visible', show);
      bar.toggleAttribute('inert', !show);
      bar.setAttribute('aria-hidden', String(!show));
    };
    // En escritorio (>=990px) Horizon hace scroll en .page-wrapper, no en window: root explícito.
    let io = null;
    const observe = () => {
      io?.disconnect();
      io = new IntersectionObserver(
        ([entry]) => {
          const rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
          setVisible(!entry.isIntersecting && entry.boundingClientRect.top < rootTop);
        },
        { root: getIntersectionRoot() }
      );
      io.observe(mainBtn);
    };
    observe();
    scrollContainerMediaQuery.addEventListener('change', observe);
    sync();
  });
}

/* ---------- Barra «Ver pedido» ---------- */

const VerPedido = {
  get el() {
    return /** @type {HTMLElement|null} */ (document.querySelector('[data-inka-ver-pedido]'));
  },
  count: -1,
  _timer: 0,

  init() {
    const el = this.el;
    if (!el) return;
    this.count = parseInt(el.querySelector('[data-inka-vp-count]')?.textContent || '0', 10) || 0;
    el.querySelector('[data-inka-ver-pedido-open]')?.addEventListener('click', openCart);

    // Cualquier cambio de líneas (añadir desde Horizon, quitar en el drawer, cantidades...).
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      /** @type {any} */ (event).promise?.then(() => this.refresh()).catch(() => this.refresh());
    });

    // Oculta mientras el drawer está abierto.
    const drawer = document.getElementById('cart-drawer');
    if (drawer) new MutationObserver(() => this.sync()).observe(drawer, { attributes: true, attributeFilter: ['open'] });
    document.addEventListener('theme-drawer:open', () => this.sync());
    document.addEventListener('theme-drawer:close', () => this.sync());

    window.addEventListener('pageshow', (e) => {
      if (e.persisted) this.refresh();
    });

    // Selector Recogida/Delivery (agente C): cuando el atributo modo_pedido ya está guardado,
    // se re-pintan el drawer y /cart para que muestren el modo y el pedido mínimo correctos.
    document.addEventListener('inka:modo-change', (e) => {
      const modo = /** @type {CustomEvent} */ (e).detail?.modo;
      rerenderCartSections(modo);
    });
    this.sync();
  },

  refresh() {
    clearTimeout(this._timer);
    this._timer = window.setTimeout(() => {
      fetchCart()
        .then((cart) => this.render(cart))
        .catch(() => {});
    }, 60);
  },

  render(cart) {
    const el = this.el;
    if (!el || !cart) return;
    const n = Number(cart.item_count) || 0;
    const countEl = el.querySelector('[data-inka-vp-count]');
    const itemsEl = el.querySelector('[data-inka-vp-items]');
    const totalEl = el.querySelector('[data-inka-vp-total]');
    if (countEl) countEl.textContent = String(n);
    if (itemsEl) itemsEl.textContent = ((n === 1 ? el.dataset.txtOne : el.dataset.txtOther) || '[n]').replace('[n]', String(n));
    if (totalEl) totalEl.textContent = money(cart.total_price, el);
    if (n > this.count && this.count >= 0 && !REDUCED.matches) {
      el.classList.remove('is-bump');
      void el.offsetWidth;
      el.classList.add('is-bump');
    }
    this.count = n;
    this.sync();
  },

  sync() {
    const el = this.el;
    if (!el) return;
    const drawerOpen = Boolean(document.querySelector('#cart-drawer[open]'));
    const show = this.count > 0 && el.dataset.template !== 'cart' && !drawerOpen;
    el.hidden = !show;
    document.body.classList.toggle('has-ver-pedido', show);
  },
};

/* ---------- Panel de añadir rápido ---------- */

const QuickAdd = {
  cache: new Map(),
  opener: /** @type {HTMLElement|null} */ (null),
  closing: false,

  get dialog() {
    return /** @type {HTMLDialogElement|null} */ (document.querySelector('[data-inka-qa]'));
  },

  init() {
    const dialog = this.dialog;
    if (!dialog) return;
    const sheet = /** @type {HTMLElement} */ (dialog.querySelector('[data-inka-qa-sheet]'));

    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.close();
    });
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) this.close();
      if (e.target instanceof Element && e.target.closest('[data-inka-qa-close]')) this.close();
    });
    dialog.addEventListener('inka:added', () => {
      this.flag(this.opener, 'ok');
      setTimeout(() => this.close(), 450);
    });

    // Deslizar hacia abajo para cerrar (móvil).
    let startY = 0;
    let dy = 0;
    let startT = 0;
    let dragging = false;
    sheet.addEventListener(
      'touchstart',
      (e) => {
        if (!MOBILE.matches || e.touches.length !== 1) return;
        const onGrab = e.target instanceof Element && e.target.closest('[data-inka-qa-grab]');
        if (sheet.scrollTop > 0 && !onGrab) return;
        if (e.target instanceof Element && e.target.closest('textarea, input, .inka-pf__pills')) return;
        dragging = true;
        startY = e.touches[0].clientY;
        startT = performance.now();
        dy = 0;
      },
      { passive: true }
    );
    sheet.addEventListener(
      'touchmove',
      (e) => {
        if (!dragging) return;
        dy = Math.max(0, e.touches[0].clientY - startY);
        if (dy > 0) {
          sheet.style.transition = 'none';
          sheet.style.transform = `translateY(${dy}px)`;
        }
      },
      { passive: true }
    );
    sheet.addEventListener('touchend', () => {
      if (!dragging) return;
      dragging = false;
      const v = dy / Math.max(1, performance.now() - startT);
      sheet.style.transition = '';
      sheet.style.transform = '';
      if (dy > 110 || (dy > 40 && v > 0.6)) this.close();
    });

    document.addEventListener('inka:quick-add', (event) => {
      const { handle, variantId, hasVariants, button } = /** @type {CustomEvent} */ (event).detail || {};
      if (!hasVariants && variantId) {
        this.direct(variantId, button);
      } else if (handle) {
        this.open(handle, button);
      }
    });
  },

  /** Estado visual del botón + de la tarjeta: 'loading' | 'ok' | 'error' | null */
  flag(button, state) {
    if (!(button instanceof HTMLElement)) return;
    if (state) button.dataset.inkaState = state;
    else delete button.dataset.inkaState;
    if (state === 'ok' || state === 'error') {
      clearTimeout(Number(button.dataset.inkaTimer || 0));
      button.dataset.inkaTimer = String(window.setTimeout(() => this.flag(button, null), 1500));
    }
  },

  async direct(variantId, button) {
    if (button?.dataset.inkaState === 'loading') return;
    this.flag(button, 'loading');
    button?.setAttribute('aria-busy', 'true');
    try {
      await add([{ id: variantId, quantity: 1 }], { source: button });
      this.flag(button, 'ok');
      announce(this.dialog?.dataset.txtAdded || '');
    } catch (error) {
      this.flag(button, 'error');
      announce(error?.message || this.dialog?.dataset.txtError || '');
    } finally {
      button?.removeAttribute('aria-busy');
    }
  },

  async open(handle, opener) {
    const dialog = this.dialog;
    if (!dialog) {
      window.location.href = `${ROOT}products/${encodeURIComponent(handle)}`;
      return;
    }
    this.opener = opener instanceof HTMLElement ? opener : null;
    const body = /** @type {HTMLElement} */ (dialog.querySelector('[data-inka-qa-body]'));
    const skeleton = /** @type {HTMLTemplateElement|null} */ (dialog.querySelector('[data-inka-qa-skeleton]'));
    if (skeleton) body.replaceChildren(skeleton.content.cloneNode(true));

    if (!dialog.open) {
      dialog.showModal();
      document.documentElement.classList.add('inka-qa-lock');
      requestAnimationFrame(() => dialog.classList.add('is-open'));
    }

    try {
      let html = this.cache.get(handle);
      if (!html) {
        const res = await fetch(`${ROOT}products/${encodeURIComponent(handle)}?section_id=inka-quick-add-ficha`, {
          credentials: 'same-origin',
        });
        if (!res.ok) throw new Error(String(res.status));
        html = await res.text();
        this.cache.set(handle, html);
      }
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const ficha = doc.querySelector('.inka-qa-ficha');
      if (!ficha) throw new Error('sin ficha');
      if (!dialog.open) return;
      body.replaceChildren(document.importNode(ficha, true));
      const first =
        body.querySelector('.inka-pill__input:checked') || body.querySelector('[data-inka-submit]:not([disabled])');
      if (first instanceof HTMLElement) first.focus({ preventScroll: true });
    } catch (_) {
      const p = document.createElement('p');
      p.className = 'inka-qa__error';
      p.setAttribute('role', 'alert');
      p.textContent = dialog.dataset.txtError || '';
      const link = document.createElement('a');
      link.href = `${ROOT}products/${encodeURIComponent(handle)}`;
      link.className = 'inka-qa__error-link';
      link.textContent = '→';
      p.append(' ', link);
      body.replaceChildren(p);
    }
  },

  close() {
    const dialog = this.dialog;
    if (!dialog || !dialog.open || this.closing) return;
    this.closing = true;
    dialog.classList.remove('is-open');
    const done = () => {
      dialog.close();
      document.documentElement.classList.remove('inka-qa-lock');
      this.closing = false;
      if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
    };
    if (REDUCED.matches) done();
    else setTimeout(done, 220);
  },
};

/* ---------- arranque ---------- */

window.InkaCart = {
  add,
  fetchCart,
  openCart,
  openQuickAdd: (handle, opener) => QuickAdd.open(handle, opener),
  refresh: () => VerPedido.refresh(),
};

VerPedido.init();
QuickAdd.init();
initPdpBars();
document.dispatchEvent(new CustomEvent('inka:cart-ready'));

// Editor de temas: re-inicializa cuando se re-pinta una sección.
document.addEventListener('shopify:section:load', () => {
  initPdpBars();
});
